import {
    type IWhatsNewResponse,
    E_ANALYSIS_CHART,
} from '@/types'
import { PATENT_EFFICIENCY_TYPE, type IRightListRequest, type IDataParam, type IChartData, type IChartResponse, type ICompanyBasicData, type IChartBasicData, type IChartPatentResponse, type ICompanyTopicInfo } from '@/types'
import { container } from '@eureka/services'
import type { ICoreDataWrapper } from '@eureka/services/src/exception'
import type { ISolutionListResponse } from '@eureka/solution-card/src/types'

export interface ICompanyResponse {
    name: string
    has_access: boolean
    entity_type: string
    logo: string
    ownership_type: string[]
    founded_date: number
    country_name?: string
    state_name?: string
    description?: string
    subsidiaries?: number
    ans_id?: string
}

export function getHeaderData(companyId: string): Promise<ICompanyResponse> {
    return container.RnDHttp.get<ICoreDataWrapper<ICompanyResponse>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/company/basic`,
        params: {
            entity_id: companyId,
        },
    }).then((res) => {
        return res.data
    })
}

interface IWhatsNewParam extends IDataParam {
    query?: string
}

export function getWhatsNewData(
    data: IWhatsNewParam
): Promise<IWhatsNewResponse[]> {
    const { child, company_id, query } = data

    return container.RnDHttp.post<ICoreDataWrapper<IWhatsNewResponse[]>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/company/new`,
        data: {
            entity_id: company_id,
            include_children: child,
            query,
        },
    }).then((res) => {
        return res.data
    });
}

interface IPaperTrendResponse {
    TARGET_PAPER_COUNT: IChartData
}
export function getPaperTrendData(data: IDataParam) {
    const { child, company_id } = data

    const url = `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/company/analysis/chart`

    return container.RnDHttp.post<ICoreDataWrapper<IPaperTrendResponse>>({
        url,
        data: {
            entity_id: company_id,
            include_children: child,
            chart_type: E_ANALYSIS_CHART.PAPER_DATETIME_TS,
            solution_type: 'PAPER',
        },
    }).then((res) => {
        return res.data
    })
}

interface IPatentTrendResponse extends IChartResponse {
    TARGET_ISSUED: IChartData
}

export function getPatentTrendData(data: IDataParam) {
    const { child, company_id } = data

    const url = `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/company/analysis/chart`

    return container.RnDHttp.post<ICoreDataWrapper<IPatentTrendResponse>>({
        url,
        data: {
            entity_id: company_id,
            include_children: child,
            chart_type: E_ANALYSIS_CHART.APD_Y_ISSUED,
        },
    }).then((res) => {
        return res.data
    })
}

interface PatentMatrixData extends IDataParam {
    type: PATENT_EFFICIENCY_TYPE
}

export function getPatentMatrixData(data: PatentMatrixData) {
    const { type, child, company_id } = data

    const isKeyEfficiency = type === PATENT_EFFICIENCY_TYPE.KEY_EFFICIENCY

    const url = `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/company/analysis/chart`

    return container.RnDHttp.post<ICoreDataWrapper<IChartResponse>>({
        url,
        data: {
            entity_id: company_id,
            include_children: child,
            chart_type: isKeyEfficiency ? E_ANALYSIS_CHART.KEY_BENEFIT_PHRASE_ADC_C : E_ANALYSIS_CHART.BENEFIT_PHRASE_ADC_C,
        },
    }).then((res) => {
        return res.data
    })
}

export function getWordCloudData(data: IDataParam) {
    const url = `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/company/analysis/chart`
    const { child, company_id } = data
    return container.RnDHttp.post<ICoreDataWrapper<IChartResponse>>({
        url,
        data: {
            entity_id: company_id,
            include_children: child,
            chart_type: E_ANALYSIS_CHART.TECH_DOMAIN_WORD_CLOUD,
        },
    }).then((res) => {
        return res.data
    })
}

export function getRightList(
    data: IRightListRequest
): Promise<ISolutionListResponse> {
    return container.RnDHttp.post<ICoreDataWrapper<ISolutionListResponse>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/company/right-list`,
        data: data,
    }).then((res) => {
        return res.data;
    });
}

export function getCompanyPatentList(
    data: IRightListRequest
): Promise<ISolutionListResponse> {
    return container.RnDHttp.post<ICoreDataWrapper<ISolutionListResponse>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/company/srp`,
        data: data,
    }).then((res) => {
        return res.data;
    });
}

export function getPaperListData(
    data: IRightListRequest
): Promise<ISolutionListResponse> {
    return container.RnDHttp.post<ICoreDataWrapper<ISolutionListResponse>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/company/srp`,
        data: data,
    }).then((res) => {
        return res.data;
    });
}

export function getCoData(params: { entity_id: string; include_children: boolean, chart_type: E_ANALYSIS_CHART }) {
    return container.RnDHttp.post<ICoreDataWrapper<IChartPatentResponse>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/company/analysis/chart`,
        data: params,
    }).then((res) => {
        return res.data;
    });
}

export function getTopicData(params: { entity_id: string; chart_type: E_ANALYSIS_CHART }) {
    return container.RnDHttp.post<ICoreDataWrapper<ICompanyTopicInfo[]>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/company/citation/tech-topic`,
        data: params,
    }).then((res) => {
        return res.data;
    });
}

export function getTechData(
    param: {
        id: string;
        topic_id?: string;
        chart_type?: string;
    }
) {
    const { id, topic_id, chart_type } = param;
    return container.RnDHttp.post<ICoreDataWrapper<IChartBasicData[]>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/company/citation`,
        data: {
            entity_id: id,
            topic_id,
            chart_type,
        },
    }).then((res) => {
        return res.data;
    });
}

export function getCompanyBasicData(id: string) {
    return container.RnDHttp.get<ICoreDataWrapper<ICompanyBasicData>>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/company/basic`,
        params: {
            entity_id: id,
        },
    }).then((res) => {
        return res.data;
    });
}
